/**
 * PaywallScreen \u2014 the Free \u2192 Pro upsell surface (PRD \u00a73 #7).
 *
 * Redesigned per issue #434 as a premium SaaS pricing page. New
 * structure:
 *
 *   1. Hero band with brand monogram + headline + value-prop +
 *      social-proof line.
 *   2. Two plan cards side-by-side (Annual / Monthly) with the
 *      Annual card visually lifted + "BEST VALUE" eyebrow.
 *   3. Restore Purchases link (quiet, not a primary CTA \u2014 per
 *      Apple App Review guidelines).
 *   4. Feature comparison matrix (Free vs Pro) grouped by section.
 *   5. FAQ accordion with 5 procurement questions.
 *   6. Testimonials row (placeholder quotes tagged
 *      data-test-placeholder for the pre-launch grep).
 *   7. Sticky purchase bar that slides up after scrolling past
 *      the plan cards.
 *   8. Apple App Review disclosures + Privacy / Terms links.
 *
 * Plan metadata + FAQ + testimonial data lives in
 * `src/lib/paywall/planContent.ts` so the same content can later
 * be exported as JSON for the iOS paywall (per acceptance #9).
 *
 * Conventions mirror the other screens:
 *   - `: ReactElement` return type.
 *   - Tailwind-less: all styles via existing class names.
 *   - `void (async () => { ... })()` for fire-and-forget promises.
 *   - Cancellation guard on every async effect.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { usePublicPath } from '../lib/routing/usePublicPath';
import { useAuth } from '../lib/auth/useAuth';

/**
 * Build-time gate for testimonial placeholders (issue #434
 * Risks). When `VITE_PAYWALL_PLACEHOLDER=true` (dev/staging) the
 * `data-test-placeholder` attribute is emitted on each
 * placeholder card so the dev tooling can find them. In
 * production (flag unset) the attribute is omitted so a
 * placeholder quote can never accidentally ship to the live
 * paywall. The placeholder copy itself still ships in dev so
 * designers can review the layout — only the marker attribute
 * is gated.
 *
 * Note: this constant is evaluated lazily inside the render
 * function (rather than as a module-level `const`) so unit
 * tests that flip `import.meta.env.VITE_PAYWALL_PLACEHOLDER`
 * via `vi.stubEnv` see the updated value. `vi.stubEnv` only
 * mutates `import.meta.env` at runtime; a frozen `const` would
 * hold the value at module-init time.
 */
function paywallPlaceholderMode(): boolean {
  return import.meta.env.VITE_PAYWALL_PLACEHOLDER === 'true';
}
import {
  ANNUAL_PLAN,
  ANNUAL_SAVINGS_PERCENT,
  APPLE_DISCLOSURES,
  FAQ_ITEMS,
  MONTHLY_PLAN,
  PAYWALL_HERO,
  PLAN_CARDS,
  TESTIMONIALS,
  type PlanCadence,
} from '../lib/paywall/planContent';
import { useProAccess } from '../lib/paywall/access';
import { BrandMonogram } from '../lib/effectiveslp/BrandMonogram';
import { SkeletonBox } from '../components/Skeleton';
import { MarketingHero } from '../components/MarketingHero';
import { PlanCard } from '../components/PlanCard';
import { FeatureTable } from '../components/FeatureTable';
import { FaqAccordion } from '../components/FaqAccordion';
import { StickyPurchaseBar } from '../components/StickyPurchaseBar';
import { humanError } from '../lib/ui/errorCopy';

interface Props {
  onBack: () => void;
  onPurchased: () => void;
}

export function PaywallScreen({ onBack, onPurchased }: Props): ReactElement {
  const { navigate } = usePublicPath();
  const { user } = useAuth();
  const { state, refresh } = useProAccess();
  const [cadence, setCadence] = useState<PlanCadence>('annual');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [openFaqIndices, setOpenFaqIndices] = useState<Set<number>>(
    () => new Set([0]),
  );
  const [barVisible, setBarVisible] = useState(false);
  const [barDismissed, setBarDismissed] = useState(false);

  const isPro = state.kind === 'pro';
  const tierLoading = state.kind === 'unknown';

  const selectedPlan =
    cadence === 'annual' ? ANNUAL_PLAN : MONTHLY_PLAN;

  // Re-select handler — keeps the documented "hide until they
  // re-select" contract for the sticky bar (StickyPurchaseBar
  // props docblock). Both the plan cards and the bar's own
  // <select> route through this so the dismissed state is
  // cleared on every cadence change, not just the first.
  function handleCadenceSelect(next: PlanCadence): void {
    setCadence(next);
    setBarDismissed(false);
  }
  const ctaLabel =
    busy
      ? 'Opening Store\u2026'
      : cadence === 'annual'
        ? 'Start annual plan'
        : 'Start monthly plan';

  // Watch scroll position \u2014 once the user scrolls past the plan
  // cards, the sticky purchase bar slides up. IntersectionObserver
  // keeps the work cheap vs a scroll listener; the sentinel is a
  // 1px marker we place just below the cards row.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sentinel = document.getElementById('paywall-sticky-sentinel');
    if (!sentinel) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback for very old engines \u2014 assume visible.
      setBarVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          // Sentinel out of view (user scrolled past) \u2192 show bar.
          setBarVisible(!entry.isIntersecting);
        }
      },
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [tierLoading]);

  function toggleFaq(index: number): void {
    setOpenFaqIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  if (tierLoading) {
    return (
      <main className="screen paywall-screen">
        <header className="screen-header">
          <button type="button" className="link-button" onClick={onBack}>
            \u2190 Back
          </button>
          <div className="auth-hero">
            <BrandMonogram size={64} />
            <div>
              <h1>EffectiveSLP Pro</h1>
              <p className="tagline">Loading your plan\u2026</p>
            </div>
          </div>
        </header>
        <div
          className="skeleton"
          role="status"
          aria-busy="true"
          aria-label="Loading Pro plans"
        >
          <SkeletonBox width="60%" height={28} />
          <SkeletonBox width="40%" height={48} style={{ marginTop: 16 }} />
          <SkeletonBox width="40%" height={48} style={{ marginTop: 8 }} />
          <SkeletonBox width="80%" height={14} style={{ marginTop: 16 }} />
          <SkeletonBox width="60%" height={14} style={{ marginTop: 8 }} />
        </div>
      </main>
    );
  }

  async function handlePurchase(): Promise<void> {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      // The actual RevenueCat purchasePackage call is iOS-native
      // and lives MBP-side. For v1.0-D from this Linux box, we
      // surface the wiring is in place + the entitlement refresh
      // runs after a (simulated) success. A real device will
      // resolve the promise via the SDK + the webhook will mirror
      // the Pro state within seconds.
      const productId =
        cadence === 'annual'
          ? 'effectiveslp_pro_annual'
          : 'effectiveslp_pro_monthly';
      setMessage(
        'Purchase flow would launch for ' +
          productId +
          ' on iOS-native (deferred to MBP step).',
      );
      await refresh();
      onPurchased();
    } catch (err) {
      setError(humanError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleRestore(): Promise<void> {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      setMessage(
        'Restore Purchases would call RevenueCat restorePurchases() on iOS-native (deferred to MBP step).',
      );
      await refresh();
    } catch (err) {
      setError(humanError(err));
    } finally {
      setBusy(false);
    }
  }

  function openExternal(href: string): void {
    navigate(href);
  }

  return (
    <main className="screen paywall-screen">
      <header className="screen-header">
        <button type="button" className="link-button" onClick={onBack}>
          \u2190 Back
        </button>
        {isPro ? (
          <div className="auth-hero">
            <BrandMonogram size={64} />
            <div>
              <h1>You\u2019re on Pro.</h1>
              <p className="tagline">
                Thanks for supporting EffectiveSLP.
              </p>
            </div>
          </div>
        ) : (
          <MarketingHero
            eyebrow={PAYWALL_HERO.socialProof}
            headline={PAYWALL_HERO.headline}
            subhead={PAYWALL_HERO.subhead}
          />
        )}
      </header>

      {!isPro && (
        <section className="paywall-plans" aria-label="Plan selection">
          <div
            className="paywall-plans-grid"
            data-testid="paywall-plans-grid"
          >
            {PLAN_CARDS.map((plan) => (
              <PlanCard
                key={plan.cadence}
                plan={plan}
                selected={cadence === plan.cadence}
                onSelect={handleCadenceSelect}
              />
            ))}
          </div>

          <button
            type="button"
            className="snap-button"
            disabled={busy}
            onClick={() => void handlePurchase()}
            data-testid="paywall-cta"
          >
            {ctaLabel}
          </button>

          <button
            type="button"
            className="link-button paywall-restore"
            disabled={busy}
            onClick={() => void handleRestore()}
            data-testid="paywall-restore"
          >
            Restore Purchases
          </button>

          <p className="paywall-savings">
            Save {ANNUAL_SAVINGS_PERCENT}% with annual billing.
          </p>
        </section>
      )}

      {isPro && (
        <section className="auth-confirm" aria-live="polite">
          <p>
            Your subscription is active. Manage it from the App Store
            subscription settings if you need to change or cancel it.
          </p>
        </section>
      )}

      <FeatureTable />

      <FaqAccordion
        items={FAQ_ITEMS}
        openIndices={openFaqIndices}
        onToggle={toggleFaq}
      />

      <section
        className="paywall-testimonials"
        aria-label="What clinicians say"
      >
        <h2 className="onboarding-eyebrow">What clinicians say</h2>
        <div className="paywall-testimonials-grid">
          {TESTIMONIALS.map((t, index) => (
            <figure
              key={`testimonial-${index}`}
              className="paywall-testimonial-card"
              {...(t.placeholder && paywallPlaceholderMode()
                ? { 'data-test-placeholder': 'true' }
                : {})}
            >
              <div
                className="paywall-testimonial-stars"
                aria-label={`Rated ${t.rating} out of 5`}
              >
                {'\u2605'.repeat(t.rating)}
              </div>
              <blockquote className="paywall-testimonial-quote">
                {t.quote}
              </blockquote>
              <figcaption className="paywall-testimonial-cite">
                <span className="paywall-testimonial-name">{t.name}</span>
                <span className="paywall-testimonial-district">
                  {t.district}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="paywall-disclosures" aria-label="Subscription terms">
        <h2 className="onboarding-eyebrow">Subscription terms</h2>
        <p className="tagline">
          {APPLE_DISCLOSURES.autoRenewDisclosure}
        </p>
        <p className="tagline">
          {APPLE_DISCLOSURES.trialDisclosure}
        </p>
        <div className="paywall-disclosure-links">
          <button
            type="button"
            className="link-button"
            onClick={() =>
              openExternal(APPLE_DISCLOSURES.privacyPolicyHref)
            }
            data-testid="paywall-privacy"
          >
            {APPLE_DISCLOSURES.privacyPolicyLabel}
          </button>
          <button
            type="button"
            className="link-button"
            onClick={() =>
              openExternal(APPLE_DISCLOSURES.termsOfServiceHref)
            }
            data-testid="paywall-terms"
          >
            {APPLE_DISCLOSURES.termsOfServiceLabel}
          </button>
        </div>
      </section>

      {error && <p className="error">{error}</p>}
      {message && <p className="tagline">{message}</p>}

      <footer className="screen-footer">
        Signed in as {user?.email ?? '(unknown)'}.
      </footer>

      {/* Sticky purchase bar \u2014 sentinel + bar. The sentinel sits
       * just below the plan cards; once it leaves the viewport
       * the IntersectionObserver flips barVisible on. */}
      {!isPro && (
        <>
          <div
            id="paywall-sticky-sentinel"
            className="paywall-sticky-sentinel"
            aria-hidden="true"
          />
          <StickyPurchaseBar
            plan={selectedPlan}
            visible={barVisible}
            dismissed={barDismissed}
            onDismiss={() => setBarDismissed(true)}
            busy={busy}
            onPurchase={() => void handlePurchase()}
            onSelect={handleCadenceSelect}
          />
        </>
      )}
    </main>
  );
}